import React, { useState } from "react";
import { FaSearch } from "react-icons/fa";
import ClienteAutocomplete from "../../../components/autocomplete/ClienteAutocomplete";
import LiquidacionEjecucionAutocomplete from "../../../components/autocomplete/LiquidacionEjecucionAutocomplete";
import { appFetchBlob } from "../../../utils/http";
import apiRoutes from "../../../api/apiRoutes";

// --- CONSTANTES ---
const FORMATO_OPCIONES = [
  { id: "EXCEL", label: "EXCEL" },
  { id: "PDF", label: "PDF" },
];

export default function ReporteDetalleFacturacionCliente() {
  const [descargando, setDescargando] = useState(false);
  const [errorValidacion, setErrorValidacion] = useState(""); // Estado para el banner

  // --- ESTADOS DEL FORMULARIO ---
  const [cliente, setCliente] = useState(null);
  const [liquidacion, setLiquidacion] = useState(null);
  const [formato, setFormato] = useState("EXCEL");

  // --- HANDLER CONSULTAR ---
  const handleConsultar = async () => {
    // Resetear error al iniciar
    setErrorValidacion("");

    // 1. VALIDACIÓN DE CAMPOS OBLIGATORIOS
    if (!cliente || !liquidacion) {
      setErrorValidacion("Debe seleccionar el cliente y nro. de liquidación");
      return;
    }

    if (!formato) {
      alert("Seleccione un formato.");
      return;
    }

    setDescargando(true);

    try {
      const params = new URLSearchParams();
      params.append("clienteId", cliente.id);
      params.append("liquidacionEjecucionId", liquidacion.id);

      const url =
        formato === "PDF"
          ? apiRoutes.liquidacionCliente.exportarPdf(params)
          : apiRoutes.liquidacionCliente.exportar(params);

      // Usamos appFetchBlob que ya maneja la autenticación y la redirección en 401
      const { blob, filename } = await appFetchBlob(
        url,
        { method: "GET" },
        { showLoading: false } // El componente ya tiene su propio indicador de carga
      );

      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[-:T]/g, "");

      const extension = formato === "PDF" ? "pdf" : "xlsx";

      a.href = downloadUrl;
      a.download = filename || `Reporte_Facturacion_${timestamp}.${extension}`;

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      // appFetchBlob lanza un error en respuestas no-OK.
      // El caso 401 es manejado dentro de http.jsx y no debería llegar aquí.
      console.error("Error en descarga:", error);
      if (error.status !== 401) {
        setErrorValidacion(error.message);
      }
    } finally {
      setDescargando(false);
    }
  };

  return (
    <div className="w-full p-4 md:p-6">
      <div className="bg-white border rounded-md shadow-sm">
        <div className="px-4 md:px-6 py-4 border-b">
          <h2 className="text-lg md:text-xl font-semibold text-gray-800">
            Detalle de Facturación de Clientes
          </h2>
        </div>

        <div className="p-4 md:p-6">
          {/* BANNER DE ERROR */}
          {errorValidacion && (
            <div className="mb-6 bg-[#F8D7DA] border border-[#f5c6cb] text-[#721c24] px-4 py-3 rounded flex items-start gap-3">
              <div className="mt-1">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="10" fill="#dc3545" />
                  <path
                    d="M15 9L9 15M9 9L15 15"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="text-sm">
                <ul className="list-disc list-inside ml-2 mt-1">
                  <li>{errorValidacion}</li>
                </ul>
              </div>
            </div>
          )}

          <div className="bg-gray-100 p-4 md:p-8 rounded-md space-y-4 md:space-y-5 max-w-4xl mx-auto">
            {/* CLIENTE */}
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
  <label className="md:w-40 shrink-0 font-bold text-gray-700">Cliente*:</label>
  <div className="w-full">
                <ClienteAutocomplete
                  value={cliente}
                  onChange={(val) => {
                    setCliente(val);
                    setErrorValidacion(""); // Limpiar error al interactuar
                  }}
                  placeholder="Seleccione un cliente..."
                />
              </div>
            </div>

            {/* LIQUIDACIÓN */}
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
  <label className="md:w-40 shrink-0 font-bold text-gray-700">Liquidación*:</label>
  <div className="w-full">
                <LiquidacionEjecucionAutocomplete
                  value={liquidacion}
                  onChange={(val) => {
                    setLiquidacion(val);
                    setErrorValidacion(""); // Limpiar error al interactuar
                  }}
                  placeholder="Seleccione una liquidación..."
                />
              </div>
            </div>

            {/* FORMATO */}
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
  <label className="md:w-40 shrink-0 font-bold text-gray-700">Formato*:</label>
  <div className="w-full">
                <select
                  className="input w-full h-[42px] bg-white border border-gray-300 rounded px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={formato}
                  onChange={(e) => setFormato(e.target.value)}
                >
                  {FORMATO_OPCIONES.map((op) => (
                    <option key={op.id} value={op.id}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-500 text-center">
            * Los campos son obligatorios
          </p>
        </div>

        <div className="px-4 md:px-6 py-4 border-t bg-gray-50 flex justify-start">
          <button
            onClick={handleConsultar}
            disabled={descargando}
            className={`flex items-center gap-2 text-white font-semibold py-2 px-6 rounded shadow transition-colors ${
              descargando
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-800 hover:bg-green-900"
            }`}
          >
            {descargando ? (
              <svg
                className="animate-spin h-4 w-4 text-white"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                ></path>
              </svg>
            ) : (
              <FaSearch className="text-sm" />
            )}
            <span>{descargando ? "Descargando..." : "Consultar"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
